import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { languageKeys } from "@op/puck-types";
import { LegalPage } from "@op/puck";
import { ContentProvider, TranslationProvider } from "@op/utils";
import { rendererClient } from "../../../lib/rendererClient";
import { contextSchema, type Context } from "../../../utils/context";
import { useTranslations } from "../../../utils/translations";
import {
  availableLocales,
  loadCatalog,
  type AvailableLocales,
} from "../../../i18n";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

const paramsSchema = z.object({
  languageKey: z.enum(languageKeys),
  legalPage: z.enum(["privacy", "imprint"]),
});

const getLegal = createServerFn({
  method: "GET",
})
  .inputValidator(z.object({ params: paramsSchema, context: contextSchema }))
  .handler(
    async ({
      data: {
        params: { languageKey },
        context: { organizationSlug, projectSlug },
      },
    }) => {
      const res = await rendererClient.languages[":organizationSlug"][
        ":projectSlug"
      ][":languageKey"].legal.$get({
        param: {
          organizationSlug,
          projectSlug,
          languageKey,
        },
      });
      if (!res.ok) {
        if (res.status === 404) throw notFound();
        throw new Error("Failed to fetch legal pages");
      }
      return await res.json();
    },
  );

export const Route = createFileRoute("/$languageKey/legal/$legalPage")({
  params: paramsSchema,
  beforeLoad: async ({ context: { languages }, params: { languageKey } }) => {
    const locale = availableLocales.includes(languageKey as AvailableLocales)
      ? (languageKey as AvailableLocales)
      : "en";
    await loadCatalog(locale);
    return { languages: languages.filter((lang) => lang !== languageKey) };
  },
  loader: async ({ params, context }) => {
    const data = await getLegal({
      data: { params, context: context as Context },
    });
    const content =
      params.legalPage === "imprint" ? data.imprint : data.privacyPolicy;
    if (!content) throw notFound();
    return { ...data, content };
  },
  head: ({ params }) => ({
    meta: [
      {
        title:
          params.legalPage === "imprint"
            ? i18n._(msg`Imprint`)
            : i18n._(msg`Privacy Policy`),
      },
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { languageKey, legalPage } = Route.useParams();
  const { languages } = Route.useRouteContext();
  const {
    content,
    organizationName,
    organizationLink,
    copyrightText,
    imprintLink,
    privacyLink,
    hasImprint,
    hasPrivacyPolicy,
    logoImage,
  } = Route.useLoaderData();
  const translations = useTranslations();

  return (
    <ContentProvider
      citations={{ content: [], excursions: {} }}
      glossaryItems={[]}
      excursions={[]}
      versions={[]}
      outline={{ content: [], excursions: {} }}
      figures={{ content: [], excursions: {} }}
    >
      <TranslationProvider {...translations}>
        <LegalPage
          heading={
            legalPage === "imprint"
              ? translations.legal.imprint
              : translations.legal.privacy
          }
          content={content}
          languageKey={languageKey}
          languages={languages}
          logoImage={logoImage}
          organizationName={organizationName ?? undefined}
          organizationLink={organizationLink ?? undefined}
          copyrightText={copyrightText ?? undefined}
          imprintLink={imprintLink ?? undefined}
          privacyLink={privacyLink ?? undefined}
          hasImprint={hasImprint}
          hasPrivacyPolicy={hasPrivacyPolicy}
          LinkComponent={Link}
        />
      </TranslationProvider>
    </ContentProvider>
  );
}
